import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@omni/ui/components/Button/button.component";
import { useMemo } from "react";
import { DefaultValues } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";
import * as yup from "yup";

import { MultiSelectField } from "@/components/Form/RHF/FormFields/MultiSelectField/multiSelectField.component";
import { PhoneField } from "@/components/Form/RHF/FormFields/PhoneField/phoneField.container";
import { StoreField } from "@/components/Form/RHF/FormFields/StoreField/storeField.container";
import { TextAreaField } from "@/components/Form/RHF/FormFields/TextAreaField/textAreaField.component";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { Messages } from "@/components/General/Messages/messages.container";
import * as PhonenumberPrefixesSelectors from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import { getShopNames } from "@/redux/modules/api/data/stores/shopNames/shopNames.selector";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { WeekDays } from "@/utils/enums";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";
import * as RHFGeneralValidationRules from "@/utils/validation/schemas/general.RHF";
import * as RHFUserValidationRules from "@/utils/validation/schemas/user.RHF";

import { OptionalPhoneSchema } from "../Form/RHF/FormFields/PhoneField/phoneField.schema";
import { StoreSchema } from "../Form/RHF/FormFields/StoreField/storeField.schema";
import { useOmniForm } from "../Form/RHF/Utils/useOmniForm";
import {
    CONTACT_QUIZZI_FAILLURE_MESSAGE_ID,
    CONTACT_QUIZZI_FORM_ID,
    CONTACT_QUIZZI_SUCCESS_MESSAGE_ID
} from "./quizziAppointment.config";
import { WeekDayOption } from "./types/contactQuizziRequest.types";

const QuizziAppointmentFormValidationSchema = yup
    .object({
        lastName: RHFUserValidationRules.lastName,
        firstName: RHFUserValidationRules.optionalFirstName,
        email: RHFUserValidationRules.emailWithNB,
        storeId: RHFGeneralValidationRules.storeId,
        message: RHFGeneralValidationRules.message(1500),
        preferredDaysOfWeek: yup.array().ensure().of(yup.mixed<WeekDays>())
    })
    .concat(StoreSchema)
    .concat(OptionalPhoneSchema);

export type ContactQuizziFormValues = yup.InferType<
    typeof QuizziAppointmentFormValidationSchema
>;

type QuizziAppointmentComponentProps = {
    loading: boolean;
    submitting: boolean;
    initialValues: DefaultValues<ContactQuizziFormValues>;
    onSubmit: (values: ContactQuizziFormValues) => Promise<void>;
};

export const QuizziAppointmentComponent = ({
    loading,
    submitting,
    initialValues,
    onSubmit
}: QuizziAppointmentComponentProps) => {
    const { t } = useGranite();
    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual
    );
    const shopNames = useSelector(getShopNames, shallowEqual);

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            email: initialValues.email,
            firstName: initialValues.firstName,
            lastName: initialValues.lastName,
            phone: initialValues.phone,
            storeId: initialValues.storeId,
            message: initialValues.message,
            preferredDaysOfWeek: initialValues.preferredDaysOfWeek || [],
            phonePrefix: initialValues.phonePrefix || "+32"
        },
        resolver: yupResolver(QuizziAppointmentFormValidationSchema),
        context: {
            phonenumberPrefixData: phonenumberPrefixes
        }
    });

    const weekDayOptions = useMemo<Array<WeekDayOption>>(
        () =>
            Object.values(WeekDays).map((weekDay) => ({
                label: t(weekDay),
                value: weekDay
            })),
        [t]
    );

    const onFormSubmit = async (values: ContactQuizziFormValues) => {
        return onSubmit(values).then(() => methods.reset());
    };

    return (
        <>
            <Messages messageid={CONTACT_QUIZZI_SUCCESS_MESSAGE_ID} />
            <Messages messageid={CONTACT_QUIZZI_FAILLURE_MESSAGE_ID} />
            <FormInfoProvider
                formId={CONTACT_QUIZZI_FORM_ID}
                withNeverbounce={true}
            >
                {({ initialized }) => (
                    <OmniFormContextProvider {...methods}>
                        <form
                            id={CONTACT_QUIZZI_FORM_ID}
                            onSubmit={methods.handleSubmit(onFormSubmit)}
                        >
                            <div className={"row"}>
                                <div className={"col-12 col-md-6"}>
                                    <TextField<ContactQuizziFormValues>
                                        name={"lastName"}
                                        label={t("general.form.lastName.label")}
                                        required={true}
                                        autoComplete={"family-name"}
                                        placeholder={t(
                                            "general.form.lastName.placeholder"
                                        )}
                                        disabled={loading || !initialized}
                                    />
                                </div>
                                <div className={"col-12 col-md-6"}>
                                    <TextField<ContactQuizziFormValues>
                                        name={"firstName"}
                                        label={t(
                                            "general.form.firstName.label"
                                        )}
                                        required={false}
                                        autoComplete={"given-name"}
                                        placeholder={t(
                                            "general.form.firstName.placeholder"
                                        )}
                                        disabled={loading || !initialized}
                                    />
                                </div>
                            </div>
                            <div className={"row"}>
                                <div className={"col-12 col-md-6"}>
                                    <TextField<ContactQuizziFormValues>
                                        name={"email"}
                                        label={t("general.form.email.label")}
                                        required={true}
                                        autoComplete={"email"}
                                        placeholder={t(
                                            "general.form.email.placeholder"
                                        )}
                                        disabled={loading || !initialized}
                                    />
                                </div>
                                <div className={"col-12 col-md-6"}>
                                    <PhoneField<ContactQuizziFormValues>
                                        autoComplete={"tel-national"}
                                        label={t(
                                            "general.form.phoneNumber.label"
                                        )}
                                        placeholder={t(
                                            "general.form.phoneNumber.placeholder"
                                        )}
                                        required={false}
                                        phonenumberPrefixes={
                                            phonenumberPrefixes
                                        }
                                        disabled={loading || !initialized}
                                    />
                                </div>
                            </div>
                            <div className={"row"}>
                                <div className={"col-12 col-md-6"}>
                                    <StoreField<ContactQuizziFormValues>
                                        name={"storeId"}
                                        label={t("general.form.store.label")}
                                        placeholder={t(
                                            "general.form.store.placeholder"
                                        )}
                                        required={true}
                                        storeList={shopNames}
                                        disabled={loading || !initialized}
                                    />
                                </div>
                                <div className={"col-12 col-md-6"}>
                                    <MultiSelectField<
                                        ContactQuizziFormValues,
                                        WeekDayOption
                                    >
                                        name={"preferredDaysOfWeek"}
                                        label={t(
                                            "general.form.preferredDaysOfWeek.label"
                                        )}
                                        placeholder={t(
                                            "general.form.preferredDaysOfWeek.placeholder"
                                        )}
                                        required={false}
                                        disabled={loading || !initialized}
                                        optionList={weekDayOptions}
                                    />
                                </div>
                            </div>
                            <div className={"row"}>
                                <div className={"col-12"}>
                                    <TextAreaField<ContactQuizziFormValues>
                                        name={"message"}
                                        label={t("general.form.message.label")}
                                        required={true}
                                        placeholder={t(
                                            "general.form.message.placeholder"
                                        )}
                                        maxLength={1500}
                                        disabled={loading || !initialized}
                                    />
                                </div>
                            </div>
                            <Button
                                type={"submit"}
                                loading={submitting}
                                disabled={loading || !initialized}
                            >
                                {t("general.form.send")}
                            </Button>
                        </form>
                    </OmniFormContextProvider>
                )}
            </FormInfoProvider>
        </>
    );
};
